import React, { useState } from 'react';
import axios from 'axios';
import { X, Calendar, Clock, User, Mail, Phone, FileText, Shield, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentModal = ({ onClose, insuranceProviders }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    service_type: '',
    insurance_provider: '',
    reason: ''
  });

  const serviceTypes = [
    'Individual Therapy',
    'Family Therapy',
    'Couple Therapy',
    'Medication Management',
    'Psychiatric Rehabilitation (PRP)',
    'Initial Assessment'
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM',
    '1:00 PM', '2:00 PM', '3:00 PM',
    '4:00 PM', '5:00 PM', '6:00 PM'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/appointments`, formData);
      setSuccess(true);
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('Failed to book appointment. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <User className="w-6 h-6 text-blue-600" />
        Personal Information
      </h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Full Name *
        </label>
        <input
          type="text"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          placeholder="John Doe"
          data-testid="appointment-full-name-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          placeholder="john@example.com"
          data-testid="appointment-email-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number *
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          placeholder="(410) 555-1234"
          data-testid="appointment-phone-input"
        />
      </div>
      <button
        onClick={() => setStep(2)}
        disabled={!formData.full_name || !formData.email || !formData.phone}
        className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
        data-testid="appointment-next-step-1-btn"
      >
        Next: Service & Schedule
      </button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Calendar className="w-6 h-6 text-blue-600" />
        Service & Schedule
      </h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Type *
        </label>
        <select
          name="service_type"
          value={formData.service_type}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          data-testid="appointment-service-type-select"
        >
          <option value="">Select a service</option>
          {serviceTypes.map((service) => (
            <option key={service} value={service}>{service}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preferred Date *
        </label>
        <input
          type="date"
          name="preferred_date"
          value={formData.preferred_date}
          onChange={handleChange}
          min={new Date().toISOString().split('T')[0]}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          data-testid="appointment-date-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preferred Time *
        </label>
        <select
          name="preferred_time"
          value={formData.preferred_time}
          onChange={handleChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          data-testid="appointment-time-select"
        >
          <option value="">Select a time</option>
          {timeSlots.map((time) => (
            <option key={time} value={time}>{time}</option>
          ))}
        </select>
      </div>
      <div className="flex gap-4">
        <button
          onClick={() => setStep(1)}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
        >
          Back
        </button>
        <button
          onClick={() => setStep(3)}
          disabled={!formData.service_type || !formData.preferred_date || !formData.preferred_time}
          className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
          data-testid="appointment-next-step-2-btn"
        >
          Next: Insurance & Details
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Shield className="w-6 h-6 text-blue-600" />
        Insurance & Additional Details
      </h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Insurance Provider (Optional)
        </label>
        <select
          name="insurance_provider"
          value={formData.insurance_provider}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          data-testid="appointment-insurance-select"
        >
          <option value="">Select insurance provider</option>
          {insuranceProviders.map((provider) => (
            <option key={provider.id} value={provider.name}>{provider.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reason for Visit (Optional)
        </label>
        <textarea
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          rows="4"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          placeholder="Please describe what brings you to seek support..."
          data-testid="appointment-reason-textarea"
        ></textarea>
      </div>
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-gray-700">
          <strong>Privacy Notice:</strong> All information is kept strictly confidential and HIPAA-compliant.
        </p>
      </div>
      <div className="flex gap-4">
        <button
          onClick={() => setStep(2)}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
          data-testid="appointment-submit-btn"
        >
          {loading ? 'Booking...' : 'Book Appointment'}
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-12 h-12 text-green-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        Appointment Request Received!
      </h3>
      <p className="text-gray-600 mb-6">
        Thank you for choosing SDORA Wellbeing. We've received your appointment request and will contact you within 24 hours to confirm your appointment.
      </p>
      <div className="bg-blue-50 rounded-lg p-6 mb-6 text-left">
        <p className="font-semibold text-gray-900 mb-2">What's Next?</p>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Check your email for confirmation details</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Our team will call to verify your appointment</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Complete your <a href="/intake" className="text-blue-600 underline hover:text-blue-700">intake forms online</a> before your visit</span>
          </li>
        </ul>
      </div>
      <button
        onClick={onClose}
        className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        data-testid="appointment-close-btn"
      >
        Close
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="appointment-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Book Your Appointment</h2>
            {!success && (
              <p className="text-sm text-gray-600 mt-1">Step {step} of 3</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="appointment-modal-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {success ? renderSuccess() : (
            <>
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;